export type ParamImportVal = boolean | string | number | object | null | undefined;

export interface IParamImportCell {
  question_label: string;
  param: string;
  val_old: ParamImportVal;
  val_new: ParamImportVal;
  changed: boolean;
  is_new_item?: boolean;
}

export interface IParamImportVerify {
  total: number;
  done: number;
  corrected: number;
  status: 'running' | 'done' | 'error';
}

export interface IParamImportPlan {
  cells: IParamImportCell[];
  /** Present once the cached sweep is shown and changed items are being re-checked against the server. */
  verify?: IParamImportVerify;
  unknownParameters: string[];
  newItemLabels: string[];
  rowsProcessed: number;
}

export const PARAM_IMPORT_MAX_RENDERED_ROWS = 500;

/** Trim Excel / item-bank labels so trailing spaces do not block lookup or look like new items. */
export const normalizeQuestionImportLabel = (label: unknown): string => String(label ?? '').trim();

export const buildQuestionsByImportLabel = <T extends { label?: string }>(items: T[]): Map<string, T> => {
  const map = new Map<string, T>();
  items.forEach(question => map.set(normalizeQuestionImportLabel(question.label), question));
  return map;
};

/**
 * Same comparison the import has always used: loose string equality, and two empty values
 * (except zero, which matters for NaN handling) are treated as no change.
 */
export const isParamImportValueChange = (val_old: ParamImportVal, val_new: ParamImportVal): boolean => {
  const isEmpty = (v: ParamImportVal) => !v && v !== 0;
  if (isEmpty(val_old) && isEmpty(val_new)) { return false; }
  const isObject = (v: ParamImportVal) => typeof v === 'object' && v !== null;
  if (isObject(val_old) || isObject(val_new)) { return JSON.stringify(val_old) !== JSON.stringify(val_new); }
  return ('' + val_old) !== ('' + val_new);
};

export const formatParamImportValue = (val: ParamImportVal): string => {
  if (val === undefined || val === null) { return ''; }
  if (typeof val === 'object') { return JSON.stringify(val); }
  return '' + val;
};

export const countChangedParamImportCells = (plan: IParamImportPlan): number =>
  plan.cells.filter(c => c.changed).length;

export const countUnchangedParamImportCells = (plan: IParamImportPlan): number =>
  plan.cells.filter(c => !c.changed).length;

export const getVisibleParamImportCells = (plan: IParamImportPlan, changedOnly: boolean): IParamImportCell[] =>
  changedOnly ? plan.cells.filter(c => c.changed) : plan.cells;

const csvEscape = (s: string): string => {
  if (/[",\r\n]/.test(s)) { return '"' + s.replace(/"/g, '""') + '"'; }
  return s;
};

/** CSV of the cells that will change (header row included). */
export const buildParamImportChangeSetCsv = (plan: IParamImportPlan): string => {
  const lines = [['question_label', 'parameter', 'current_value', 'new_value', 'new_item'].join(',')];
  plan.cells.filter(c => c.changed).forEach(c => {
    lines.push([
      c.question_label,
      c.param,
      formatParamImportValue(c.val_old),
      formatParamImportValue(c.val_new),
      c.is_new_item ? 'true' : 'false',
    ].map(csvEscape).join(','));
  });
  return lines.join('\r\n');
};

const cellKey = (c: IParamImportCell) => `${c.question_label}\u0000${c.param}`;

/** How many cells flipped between Changed and No change after re-checking against fresh server values. */
export const countChangedFlips = (before: IParamImportCell[], after: IParamImportCell[]): number => {
  const prior = new Map<string, boolean>();
  before.forEach(c => prior.set(cellKey(c), c.changed));
  let flips = 0;
  after.forEach(c => {
    const was = prior.get(cellKey(c));
    if (was !== undefined && was !== c.changed) { flips++; }
  });
  return flips;
};
